import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import ExpertCard from '../Card/ExpertCard';
import { T } from '@tolgee/react';
import Loader from '../Loader/Loader';
import type { ExpertData } from '@/types/expert';
import { DEFAULT_STRAPI_SORT, getExperts } from '@/services/strapiService';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';

type CirclePosition = { pos: number; color: string };

const shuffleArray = (array: number[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const generateCirclePositions = (length: number): CirclePosition[] => {
  const indices = shuffleArray(Array.from({ length }, (_, i) => i));
  const positions: CirclePosition[] = [];

  const blueCount = Math.floor(length / 6);
  const blackCount = Math.floor(length / 6);

  for (let i = 0; i < blueCount; i++) {
    const pos = indices.pop();
    if (pos !== undefined) positions.push({ pos, color: 'bg-primary' });
  }

  for (let i = 0; i < blackCount; i++) {
    const pos = indices.pop();
    if (pos !== undefined) positions.push({ pos, color: 'bg-black' });
  }

  return positions.sort((a, b) => a.pos - b.pos);
};

const OurExperts: React.FC = () => {
  const [experts, setExperts] = useState<ExpertData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const locale = useSelectedLanguage();

  useEffect(() => {
    const fetchExperts = async () => {
      try {
        const response = await getExperts(30, 0, {}, locale, DEFAULT_STRAPI_SORT);
        setExperts(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching experts:', error);
        setLoading(false);
      }
    };

    void fetchExperts();
  }, []);

  const circlePositions = generateCirclePositions(experts.length);

  if (loading) {
    return <Loader position={'absolute'} />; // Show loader while fetching data
  }

  return (
    <section className="py-16 overflow-hidden">
      <div className="text-center mb-12 max-w-lg m-auto">
        <h3 className="text-sm font-semibold text-primary uppercase">
          <T>home.additional_support_options.experts.label</T>
        </h3>
        <h2 className="text-6xl mt-2.5">
          <T>home.experts_section.title.label</T>
        </h2>
        <p className="text-base mt-2.5">
          <T>home.experts_section.text.label</T>
        </p>
        <div className="w-48 m-auto align-middle mt-8">
          <Button variant="primary" href="/experts">
            <T>home.experts_section.button.find_expert.label</T>
          </Button>
        </div>
      </div>
      <div className="group flex flex-wrap justify-left gap-4 w-[125%] m-auto -ml-[10%] max-h-[225px] overflow-hidden transition-transform duration-500 hover:-translate-x-4">
        {experts.map((expert, index) => (
          <React.Fragment key={expert.id}>
            {circlePositions.map(
              (circle, i) =>
                circle.pos === index && (
                  <div
                    key={i}
                    className={`flex items-center justify-center w-12 h-12 rounded-full ${circle.color}`}
                  ></div>
                ),
            )}
            <ExpertCard
              imageSrc={expert.attributes.picture.data?.attributes.formats.thumbnail?.url || ''}
              name={expert.attributes.name}
            />
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default OurExperts;
